import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../theme/getTheme';
import { PALETTE } from '../CoinStoreModal/CoinStoreModal.constants';

export const proUpgradeCardStyles = (theme: Theme) => StyleSheet.create({
  card: {
    borderRadius: 16,
    borderWidth: 2.5,
    borderColor: `${theme.primary}80`,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 4px 0 ${PALETTE.cardShadow}, 0 10px 20px rgba(0,0,0,0.5), 0 0 28px ${theme.primary}33, inset 0 0 0 2px rgba(255,255,255,0.08)` }
      : { shadowColor: theme.primary, shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.4, shadowRadius: 16, elevation: 12 }),
  },
  gradientFill: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
  },
  inner: {
    padding: 14,
  },
  // ── Header row ──────────────────────────────────────────────────
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  crownCircle: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: `${theme.accent}22`,
    borderWidth: 1.5,
    borderColor: `${theme.accent}50`,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  crownImg: {
    width: 20,
    height: 20,
  },
  planName: {
    flex: 1,
    fontSize: 20,
    fontWeight: '900',
    color: theme.white,
    letterSpacing: -0.4,
    textShadowColor: 'rgba(29,15,77,0.8)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 2,
  },
  proBadge: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 999,
    backgroundColor: theme.accent,
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 2px 0 ${PALETTE.earnIconBorder}` }
      : { shadowColor: PALETTE.earnIconBorder, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 1, shadowRadius: 0, elevation: 2 }),
  },
  proBadgeText: {
    fontSize: 9,
    fontWeight: '900',
    color: '#1A1208',
    letterSpacing: 0.6,
  },
  activeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 999,
    backgroundColor: theme.secondary,
  },
  activeBadgeText: { fontSize: 9, fontWeight: '900', color: '#062417', letterSpacing: 0.6 },
  // ── Billing toggle ─────────────────────────────────────────────
  toggleRow: {
    flexDirection: 'row',
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: PALETTE.cardBorder,
    padding: 3,
    marginBottom: 14,
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 3px 0 ${PALETTE.cardShadow}, inset 0 1px 0 rgba(255,255,255,0.04)` }
      : { shadowColor: PALETTE.cardShadow, shadowOffset: { width: 0, height: 3 }, shadowOpacity: 1, shadowRadius: 0, elevation: 3 }),
  },
  toggleBtn: {
    flex: 1,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleBtnActive: {
    backgroundColor: theme.accent,
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 2px 0 ${PALETTE.earnIconBorder}, inset 0 1px 0 rgba(255,255,255,0.3)` }
      : { shadowColor: PALETTE.earnIconBorder, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 1, shadowRadius: 0, elevation: 2 }),
  },
  toggleBtnInactive: { backgroundColor: 'transparent' },
  toggleBtnPressed: { transform: [{ translateY: 1 }], opacity: 0.85 },
  toggleBtnInner: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  toggleBtnLabel: { fontSize: 13, fontWeight: '900', letterSpacing: 0.2 },
  toggleBtnLabelActive: { color: '#1A1208' },
  toggleBtnLabelInactive: { color: 'rgba(255,255,255,0.5)' },
  savePill: {
    backgroundColor: PALETTE.earnIconBorder,
    borderRadius: 999,
    paddingVertical: 2,
    paddingHorizontal: 5,
  },
  savePillText: { fontSize: 9, fontWeight: '900', color: theme.accent, letterSpacing: 0.3 },
  // ── Price ──────────────────────────────────────────────────────
  priceBlock: { alignItems: 'flex-start', marginBottom: 12 },
  priceRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 0 },
  priceDollar: {
    fontSize: 18,
    fontWeight: '900',
    color: theme.white,
    lineHeight: 42,
    textShadowColor: 'rgba(29,15,77,0.8)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 0,
  },
  priceAmount: {
    fontSize: 40,
    fontWeight: '900',
    color: theme.white,
    letterSpacing: -1,
    lineHeight: 46,
    textShadowColor: 'rgba(29,15,77,0.8)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 0,
  },
  pricePer: {
    fontSize: 14,
    fontWeight: '800',
    color: 'rgba(255,255,255,0.55)',
    lineHeight: 22,
    marginBottom: 4,
    marginLeft: 2,
  },
  priceSub: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.45)',
    fontWeight: '700',
    marginTop: 1,
  },
  // ── Divider ────────────────────────────────────────────────────
  divider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
    marginHorizontal: -14,
  },
  // ── Perks ──────────────────────────────────────────────────────
  perkList: { gap: 8, marginBottom: 16 },
  perkRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  perkCheck: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: `${theme.accent}20`,
    borderWidth: 1.5,
    borderColor: `${theme.accent}50`,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  perkLabel: { fontSize: 13, fontWeight: '700', color: 'rgba(255,255,255,0.88)', flex: 1 },
  // ── CTA ────────────────────────────────────────────────────────
  ctaBtn: {
    width: '100%',
    paddingVertical: 13,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: PALETTE.buyBorder,
    overflow: 'hidden',
    marginBottom: 8,
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 3px 0 ${PALETTE.buyBorder}, inset 0 1px 0 rgba(255,255,255,0.35)` }
      : { shadowColor: PALETTE.buyBorder, shadowOffset: { width: 0, height: 3 }, shadowOpacity: 1, shadowRadius: 0, elevation: 3 }),
  },
  ctaBtnGradient: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
  },
  ctaBtnPressed: { transform: [{ translateY: 2 }], opacity: 0.9 },
  ctaBtnLoading: { opacity: 0.6 },
  ctaBtnText: {
    fontSize: 16,
    fontWeight: '900',
    color: theme.white,
    letterSpacing: 0.2,
    textShadowColor: PALETTE.buyBorder,
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 0,
  },
  manageBtn: {
    width: '100%',
    paddingVertical: 10,
    borderRadius: 10,
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.10)',
    marginBottom: 8,
  },
  manageBtnText: { fontSize: 13, fontWeight: '700', color: 'rgba(255,255,255,0.55)' },
  manageBtnPressed: { opacity: 0.7 },
  renewalNote: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.3)',
    fontWeight: '600',
    textAlign: 'center',
  },
});
